// Simplified repo functions - database tables need to be created
import { supabase } from '@/integrations/supabase/client';
import {
  FALLBACK_ACCELERATOR,
  FALLBACK_CONFIG,
  FALLBACK_SERVER,
  buildFallbackCatalog,
  buildFallbackModels,
  buildFallbackProfiles,
  buildFallbackThroughput,
  cloneModel,
  cloneProfiles,
} from './fallbackCatalog';
import { canonicalizeThroughputBackend } from './db/throughput';
import type {
  Accelerator,
  Model,
  Precision,
  ProfileSeed,
  Server,
  SizingConfig,
  ThroughputPoint,
} from '../types';

type SupabaseLikeClient = {
  from: (table: string) => {
    select: (query?: string) => Promise<{
      data: unknown[] | null;
      error: { message?: string } | null;
    }>;
  };
};

const supabaseAny = supabase as unknown as SupabaseLikeClient;

const VALID_PRECISIONS: Precision[] = [4, 8, 16, 32];
const MODEL_TABLE_CANDIDATES = ['model_catalog_models'];
const THROUGHPUT_TABLE_CANDIDATES = ['model_catalog_throughput'];
const PROFILE_TABLE_CANDIDATES = ['personas'];

export { FALLBACK_ACCELERATOR, FALLBACK_CONFIG, FALLBACK_SERVER, buildFallbackCatalog };

function isValidPrecision(value: unknown): value is Precision {
  return VALID_PRECISIONS.includes(value as Precision);
}

function toNumber(value: unknown): number | null {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value;
  }
  if (typeof value === 'string') {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  }
  return null;
}

function toDecimalPercent(value: number | null): number | null {
  if (value === null || Number.isNaN(value) || !Number.isFinite(value)) {
    return null;
  }

  const absolute = Math.abs(value);
  if (absolute > 1) {
    return value / 100;
  }

  return value;
}

function normalizeSizingConfig(config: SizingConfig): SizingConfig {
  const normalizedMemory = toDecimalPercent(config.memory_headroom_pct) ?? 0;
  const normalizedThroughput = toDecimalPercent(config.throughput_headroom_pct) ?? 0;

  return {
    ...config,
    memory_headroom_pct: normalizedMemory,
    throughput_headroom_pct: normalizedThroughput,
  };
}

function parseJsonRecord(value: unknown): Record<string, unknown> | null {
  if (!value) {
    return null;
  }
  if (typeof value === 'string') {
    try {
      const parsed = JSON.parse(value);
      return typeof parsed === 'object' && parsed !== null ? parsed : null;
    } catch (error) {
      console.warn('[repo] Failed to parse JSON value from Supabase', error);
      return null;
    }
  }
  if (typeof value === 'object') {
    return value as Record<string, unknown>;
  }
  return null;
}

function parsePrecisionMap(value: unknown): Partial<Record<Precision, number>> {
  const record = parseJsonRecord(value);
  if (!record) {
    return {};
  }

  const result = Object.entries(record).reduce<Partial<Record<Precision, number>>>((acc, [key, rawValue]) => {
    const precision = Number(key);
    if (isValidPrecision(precision)) {
      const numeric = toNumber(rawValue);
      if (numeric !== null) {
        acc[precision] = numeric;
      }
    }
    return acc;
  }, {});

  return result;
}

async function fetchSupabaseTable(table: string): Promise<{ data: unknown[] | null; error: Error | null }> {
  try {
    if (!supabaseAny?.from) {
      return { data: null, error: new Error('Supabase client not initialized') };
    }
    const { data, error } = await supabaseAny.from(table).select('*');
    console.log(`[repo] DEBUG fetchSupabaseTable("${table}"):`, {
      dataLength: data?.length,
      error: error?.message,
      firstRow: data?.[0]
    });
    if (error) {
      console.warn(`[repo] Failed to load ${table} from Supabase`, error.message ?? error);
      return { data: null, error: new Error(error.message ?? 'Supabase query failed') };
    }
    if (!data || data.length === 0) {
      console.info(`[repo] Table ${table} is empty, will use fixtures`);
      return { data: null, error: null }; // Empty is NOT an error
    }
    return { data, error: null };
  } catch (error) {
    console.warn(`[repo] Unexpected error querying Supabase table ${table}`, error);
    return { data: null, error: error instanceof Error ? error : new Error(String(error)) };
  }
}

async function fetchFirstAvailableTable(candidates: string[]): Promise<{ data: unknown[] | null; error: Error | null }> {
  // Now that we know the correct table names, just fetch the first (and only) candidate
  const table = candidates[0];
  return await fetchSupabaseTable(table);
}

function normalizeModelRow(row: Record<string, unknown>): Model | null {
  const modelId = typeof row.model_id === 'string' ? row.model_id : null;
  const family = typeof row.family === 'string' ? row.family : null;
  const displayName = typeof row.display_name === 'string' ? row.display_name : null;
  const params = toNumber(row.params_b);
  const seqLen = toNumber(row.max_seq_len);
  const baseRuntime = toNumber(row.base_runtime_overhead_bytes);

  if (!modelId || !family || !displayName || params === null || seqLen === null || baseRuntime === null) {
    return null;
  }

  const normalized = {
    model_id: modelId,
    family,
    display_name: displayName,
    params_b: params / 1_000_000_000, // Convert from full count to billions
    max_seq_len: seqLen,
    weights_mem_json: parsePrecisionMap(row.weights_mem_json),
    kv_per_token_json: parsePrecisionMap(row.kv_per_token_json),
    base_runtime_overhead_bytes: baseRuntime,
    meta:
      parseJsonRecord(row.meta) ??
      (typeof row.meta === 'object' && row.meta !== null
        ? (row.meta as Record<string, unknown>)
        : null),
  };

  console.debug('[repo] Normalized model row', {
    modelId,
    raw: row,
    normalized,
  });

  return normalized;
}

function normalizeThroughputRow(row: Record<string, unknown>): ThroughputPoint | null {
  const modelId = typeof row.model_id === 'string' ? row.model_id : null;
  const batch = toNumber(row.batch);
  const promptRaw = toNumber(row.prompt_tps);
  const decodeRaw = toNumber(row.decode_tps);
  const precisionRaw = toNumber(row.precision);
  const backend = canonicalizeThroughputBackend(row.backend);
  const id = toNumber(row.id);

  if (
    !modelId ||
    batch === null ||
    promptRaw === null ||
    decodeRaw === null ||
    precisionRaw === null ||
    !backend
  ) {
    console.warn('[repo] Rejecting throughput row missing required fields', {
      modelId,
      backendRaw: row.backend,
      batch,
      promptRaw,
      decodeRaw,
      precisionRaw,
      hasBackend: Boolean(backend),
      row,
    });
    return null;
  }

  const precision = Math.trunc(precisionRaw);
  if (!isValidPrecision(precision)) {
    console.warn('[repo] Rejecting throughput row with invalid precision', {
      modelId,
      backend,
      batch,
      precisionRaw,
      row,
    });
    return null;
  }

  const normalized = {
    id: id ?? undefined,
    model_id: modelId,
    backend,
    precision: precision as Precision,
    batch,
    prompt_tps: promptRaw,   // per GPU
    decode_tps: decodeRaw,   // per GPU
  };

  console.debug('[repo] Normalized throughput row', {
    modelId,
    backend,
    batch,
    precision,
    prompt_tps: normalized.prompt_tps,
    decode_tps: normalized.decode_tps,
    raw: row,
  });

  return normalized;
}


// Persona seeds with latency profiles
export interface PersonaSeed {
  id: string;
  name: string;
  display_name: string;
  description?: string;
  role?: string;
  prompt_tokens_per_turn: number;
  response_tokens_per_turn: number;
  turns_kept: number;
  read_sec_default: number;
  think_sec_default: number;
  latency_slo_default_ms: number;
  latency_profiles: {
    snappy: { ttft_ms: number; decode_tps: number };
    fast: { ttft_ms: number; decode_tps: number };
    resp: { ttft_ms: number; decode_tps: number };
    comfy: { ttft_ms: number; decode_tps: number };
    async: { ttft_ms: number; decode_tps: number };
  };
  mix_percent?: number | null;
}

export async function getPersonas(): Promise<PersonaSeed[]> {
  const result = await fetchSupabaseTable('personas');
  if (result.error) {
    console.warn('[repo] Database error fetching personas, returning empty list', result.error);
    return [];
  }
  if (!result.data) return [];

  const personas = (result.data as Record<string, unknown>[])
    .filter((row) => row.is_active !== false)
    .map((row) => {
      const id = typeof row.id === 'string' ? row.id : null;
      const name = typeof row.name === 'string' ? row.name : null;
      const displayName = typeof row.display_name === 'string' ? row.display_name : name;
      const prompt = toNumber(row.prompt_tokens_per_turn);
      const response = toNumber(row.response_tokens_per_turn);
      const turns = toNumber(row.turns_kept) ?? 1;
      const readSec = toNumber(row.read_sec_default);
      const thinkSec = toNumber(row.think_sec_default);
      const latencyDefault = toNumber(row.default_latency_slo_ms) ?? 0;

      if (!id || !displayName || prompt === null || response === null || readSec === null || thinkSec === null) {
        return null;
      }

      const latency_profiles = {
        snappy: { ttft_ms: toNumber(row.ttft_snappy_ms) ?? 0, decode_tps: toNumber(row.decode_tps_snappy) ?? 0 },
        fast: { ttft_ms: toNumber(row.ttft_fast_ms) ?? 0, decode_tps: toNumber(row.decode_tps_fast) ?? 0 },
        resp: { ttft_ms: toNumber(row.ttft_resp_ms) ?? 0, decode_tps: toNumber(row.decode_tps_resp) ?? 0 },
        comfy: { ttft_ms: toNumber(row.ttft_comfy_ms) ?? 0, decode_tps: toNumber(row.decode_tps_comfy) ?? 0 },
        async: { ttft_ms: toNumber(row.ttft_async_ms) ?? 0, decode_tps: toNumber(row.decode_tps_async) ?? 0 },
      };

      return {
        id,
        name: name ?? displayName,
        display_name: displayName ?? id,
        description: typeof row.description === 'string' ? row.description : undefined,
        role: typeof row.role === 'string' ? row.role : undefined,
        prompt_tokens_per_turn: prompt,
        response_tokens_per_turn: response,
        turns_kept: turns,
        read_sec_default: readSec,
        think_sec_default: thinkSec,
        latency_slo_default_ms: latencyDefault,
        latency_profiles,
        mix_percent: toNumber(row.mix_percent),
      } as PersonaSeed;
    })
    .filter((p): p is PersonaSeed => Boolean(p));

  if (personas.length) {
    console.debug('[repo] Example normalized persona', personas[0]);
  }
  return personas;
}


// These functions will work once the database tables are created
// For now, they return mock data or throw errors
export async function getAccelerator(): Promise<Accelerator> {
  const result = await fetchSupabaseTable('model_catalog_accelerators');
  if (result.error) {
    console.warn('[repo] Database error fetching accelerator, using fixtures', result.error);
    return { ...FALLBACK_ACCELERATOR };
  }

  if (result.data && result.data.length > 0) {
    const row = result.data[0] as Record<string, unknown>;
    const acceleratorId = typeof row.accelerator_id === 'string' ? row.accelerator_id : 'gaudi3';
    const backend = typeof row.backend === 'string' ? row.backend : 'gaudi3_pcie';
    const displayName = typeof row.display_name === 'string' ? row.display_name : 'Gaudi3';
    const hbm = toNumber(row.hbm_bytes);
    const reserved = toNumber(row.reserved_runtime_bytes);
    const bridgeGroupSizeMax = toNumber(row.bridge_group_size_max);

    if (hbm !== null && reserved !== null) {
      return {
        accelerator_id: acceleratorId,
        backend,
        hbm_bytes: hbm,
        reserved_runtime_bytes: reserved,
        bridge_group_size_max: bridgeGroupSizeMax ?? undefined,
        display_name: displayName,
        meta: null,
      };
    }
  }

  console.info('[repo] No accelerator data in database, using fixtures');
  return { ...FALLBACK_ACCELERATOR };
}

export async function getServerXE7740(): Promise<Server> {
  const result = await fetchSupabaseTable('model_catalog_servers');
  if (result.error) {
    console.warn('[repo] Database error fetching server, using fixtures', result.error);
    return { ...FALLBACK_SERVER };
  }

  if (result.data && result.data.length > 0) {
    const row = result.data[0] as Record<string, unknown>;
    const serverSku = typeof row.server_sku === 'string' ? row.server_sku : 'XE7740';
    const acceleratorId = typeof row.accelerator_id === 'string' ? row.accelerator_id : 'gaudi3';
    const displayName = typeof row.display_name === 'string' ? row.display_name : 'Server';
    const dellLink = typeof row.dell_link_url === 'string' ? row.dell_link_url : null;
    const accelSlots = toNumber(row.accel_slots);

    if (accelSlots !== null) {
      return {
        server_sku: serverSku,
        accelerator_id: acceleratorId,
        accel_slots: accelSlots,
        display_name: displayName,
        dell_link_url: dellLink,
        meta: null,
      };
    }
  }

  console.info('[repo] No server data in database, using fixtures');
  return { ...FALLBACK_SERVER };
}

export async function getModels(): Promise<Model[]> {
  const result = await fetchFirstAvailableTable(MODEL_TABLE_CANDIDATES);

  if (result.error) {
    console.warn('[repo] Database error fetching models, using fixtures', result.error);
    return buildFallbackModels();
  }

  if (result.data && result.data.length > 0) {
    const models: Model[] = [];
    for (const raw of result.data as Record<string, unknown>[]) {
      const normalized = normalizeModelRow(raw);

      if (!normalized) {
        const missingRequired: string[] = [];
        if (typeof raw.model_id !== 'string') missingRequired.push('model_id');
        if (typeof raw.family !== 'string') missingRequired.push('family');
        if (typeof raw.display_name !== 'string') missingRequired.push('display_name');
        if (toNumber(raw.params_b) === null) missingRequired.push('params_b');
        if (toNumber(raw.max_seq_len) === null) missingRequired.push('max_seq_len');
        if (toNumber(raw.base_runtime_overhead_bytes) === null) {
          missingRequired.push('base_runtime_overhead_bytes');
        }

        console.error('[repo] ⚠️ Catalog model row missing required metadata, excluding from results', {
          modelId: typeof raw.model_id === 'string' ? raw.model_id : undefined,
          missingRequired,
        });
        continue;
      }

      const missingFields: string[] = [];

      if (Object.keys(normalized.weights_mem_json).length === 0) {
        missingFields.push('weights_mem_json');
      }

      if (Object.keys(normalized.kv_per_token_json).length === 0) {
        missingFields.push('kv_per_token_json');
      }

      if (missingFields.length > 0) {
        console.error('[repo] ⚠️ Catalog model missing required data, excluding from results', {
          modelId: normalized.model_id,
          displayName: normalized.display_name,
          missingFields,
        });
        continue;
      }

      models.push(normalized);
    }

    if (models.length > 0) {
      console.debug('[repo] Returning normalized models (count)', models.length);
      return models
        .map(cloneModel)
        .sort((a, b) => a.display_name.localeCompare(b.display_name));
    }
  }

  console.info('[repo] No models in database, using fixtures');
  return buildFallbackModels();
}

export async function getThroughput(): Promise<ThroughputPoint[]> {
  const result = await fetchFirstAvailableTable(THROUGHPUT_TABLE_CANDIDATES);

  if (result.error) {
    console.warn('[repo] Database error fetching throughput, using fixtures', result.error);
    return buildFallbackThroughput();
  }

  if (result.data && result.data.length > 0) {
    const rawRows = result.data as Record<string, unknown>[];
    const throughput = rawRows.map(normalizeThroughputRow).filter((point): point is ThroughputPoint => Boolean(point));

    if (throughput.length !== rawRows.length) {
      console.warn('[repo] Some throughput rows were dropped during normalization', {
        rawCount: rawRows.length,
        normalizedCount: throughput.length,
      });
    }

    if (throughput.length > 0) {
      const sample = throughput[0];
      console.debug('[repo] Throughput sample (post-normalization)', {
        count: throughput.length,
        modelId: sample.model_id,
        backend: sample.backend,
        batch: sample.batch,
        precision: sample.precision,
        prompt_tps: sample.prompt_tps,
        decode_tps: sample.decode_tps,
      });

      // Sort deterministically by model_id, precision, batch, backend
      return throughput.sort((a, b) => {
        if (a.model_id !== b.model_id) {
          return a.model_id.localeCompare(b.model_id);
        }
        if (a.precision !== b.precision) {
          return a.precision - b.precision;
        }
        if (a.batch !== b.batch) {
          return a.batch - b.batch;
        }
        return a.backend.localeCompare(b.backend);
      });
    }

    console.error('[repo] ⚠️ Throughput table had rows but none survived normalization; using fixtures');
  }

  console.info('[repo] No throughput data in database, using fixtures');
  return buildFallbackThroughput();
}

export async function getProfiles(): Promise<ProfileSeed[]> {
  const result = await fetchFirstAvailableTable(PROFILE_TABLE_CANDIDATES);

  if (result.error) {
    console.warn('[repo] Database error fetching profiles, using fixtures', result.error);
    return buildFallbackProfiles();
  }

  // if (result.data && result.data.length > 0) {
  //   const profiles = (result.data as Record<string, unknown>[])
  //     .map(normalizeProfileRow)
  //     .filter((profile): profile is ProfileSeed => Boolean(profile));

  //   if (profiles.length > 0) {
  //     return cloneProfiles(profiles).sort((a, b) => a.display_name.localeCompare(b.display_name));
  //   }
  // }

  console.info('[repo] No profiles in database, using fixtures');
  return buildFallbackProfiles();
}

export async function getConfig(): Promise<SizingConfig> {
  const result = await fetchSupabaseTable('model_catalog_config');

  if (result.error) {
    console.warn('[repo] Database error fetching config, using fixtures', result.error);
    return normalizeSizingConfig({ ...FALLBACK_CONFIG });
  }

  if (result.data && result.data.length > 0) {
    const row = result.data[0] as Record<string, unknown>;
    const memoryHeadroom = toNumber(row.memory_headroom_pct);
    const throughputHeadroom = toNumber(row.throughput_headroom_pct);
    const id = typeof row.id === 'boolean' ? row.id : true;

    if (memoryHeadroom !== null && throughputHeadroom !== null) {
      return normalizeSizingConfig({
        id,
        memory_headroom_pct: memoryHeadroom,
        throughput_headroom_pct: throughputHeadroom,
      });
    }
  }

  console.info('[repo] No config data in database, using fixtures');
  return normalizeSizingConfig({ ...FALLBACK_CONFIG });
}
